import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Skeleton,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AssignmentIcon from '@mui/icons-material/Assignment';
import QuizIcon from '@mui/icons-material/Quiz';
import Error403 from '../Error/403';
import AssessmentQuestionModify from 'src/pages/QuizManagement/AssessmentQuestionModify';
import * as XLSX from 'xlsx';

const QuizEdit = ({ ...rest }) => {
  const [quizList, setQuizList] = useState([]);
  const [quizType, setQuizType] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [searchTerm, setSearchTerm] = useState('');
  const [downloadLoading, setDownloadLoading] = useState(null);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    fetch(`${process.env.REACT_APP_API_URL}/api/quizdetails/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('token'),
      },
    })
      .then((resp) => resp.json())
      .then((data) => {
        if (!isMounted) return;
        if (data.detail) {
          setError('Failed to load assessments');
        } else {
          const activeQuizzes = data.filter(
            (item) => item.status === 'Active' && item.organization_type === 'Individual'
          );
          setQuizList(activeQuizzes.map((item) => item.name));
          setQuizType(
            activeQuizzes.map((item) => ({
              name: item.name,
              organization_type: item.organization_type,
            }))
          );
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError('Failed to load assessments. Please try again.');
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredQuizList = useMemo(() => {
    if (!searchTerm.trim()) return quizList;
    const searchLower = searchTerm.toLowerCase();
    return quizList.filter((quiz) => quiz.toLowerCase().includes(searchLower));
  }, [quizList, searchTerm]);

  const paginatedQuizList = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredQuizList.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredQuizList, page, rowsPerPage]);

  const stats = useMemo(() => {
    return {
      total: quizList.length,
      visible: filteredQuizList.length,
    };
  }, [quizList.length, filteredQuizList.length]);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    setPage(0);
  }, []);

  const saveExcelFile = useCallback((buffer, filename) => {
    const data = new Blob([buffer], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(link);
  }, []);

  const handleDownload = useCallback(
    (quizName) => {
      setDownloadLoading(quizName);
      setError(null);

      fetch(`${process.env.REACT_APP_API_URL}/api/quizdetailbyassessementname/${quizName}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + localStorage.getItem('token'),
        },
      })
        .then((resp) => resp.json())
        .then((data) => {
          if (data.detail) {
            setError('Failed to download assessment questions');
          } else {
            const excelData = data[0].quizdetails.map((row) => Object.values(row));
            const worksheet = XLSX.utils.aoa_to_sheet(excelData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Quiz Questions');

            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            saveExcelFile(excelBuffer, `${quizName}.xlsx`);
            setDownloadSuccess(true);
          }
        })
        .catch((err) => {
          setError('Failed to download assessment. Please try again.');
        })
        .finally(() => {
          setDownloadLoading(null);
        });
    },
    [saveExcelFile]
  );

  const handleSnackbarClose = useCallback((event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setDownloadSuccess(false);
  }, []);

  const renderSkeleton = useMemo(
    () => (
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {Array.from({ length: 8 }).map((_, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            <Card variant="outlined">
              <CardContent>
                <Skeleton variant="text" width="80%" height={32} sx={{ mx: 'auto', mb: 2 }} />
                <Stack direction="row" spacing={1} justifyContent="center">
                  <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: '50px' }} />
                  <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: '50px' }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    ),
    []
  );

  const renderEmptyState = useMemo(
    () => (
        <Box
          sx={{
          mt: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
        }}
      >
        <QuizIcon sx={{ fontSize: 64, mb: 1, color: 'text.secondary' }} />
        <Typography variant="h6">No assessments available</Typography>
        <Typography variant="body2" align="center" sx={{ maxWidth: 360, mt: 1 }}>
          {searchTerm
            ? 'No assessments match your search. Try adjusting your search terms.'
            : 'No customer owned assessments found. Create assessments to get started.'}
          </Typography>
      </Box>
    ),
    [searchTerm]
  );

  return (
    <>
      <Helmet>
        <title>LabAsService - Edit Assessment Questions</title>
      </Helmet>
      {localStorage.getItem('user_access') === 'You do not have permission to perform this action.' &&
      localStorage.getItem('group') !== 'trainer' &&
      localStorage.getItem('group') !== 'admin' ? (
        <Error403 />
      ) : (
        <Container maxWidth={false} sx={{ py: 6, pb: 8, px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 } }}>
          <Card {...rest} sx={{ mb: 4 }}>
              <Box
                sx={{
                background: 'linear-gradient(135deg, rgba(46,125,50,0.12), rgba(21,101,192,0.12))',
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                p: { xs: 2.5, md: 3 },
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <AssignmentIcon color="primary" sx={{ fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" component="h1">
                  Edit Assessment Questions
                </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage and modify customer owned assessment questions
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Divider />

            <Grid container spacing={2} sx={{ px: { xs: 2.5, md: 3 }, py: 2.5 }}>
              <Grid item xs={12} md={6} lg={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Stack spacing={1} alignItems="flex-start">
                      <QuizIcon color="primary" fontSize="large" />
                      <Typography variant="subtitle2" color="text.secondary">
                        Total assessments
                      </Typography>
                      <Typography variant="h4">{stats.total}</Typography>
                      <Chip label={`${stats.visible} visible`} color="primary" size="small" />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6} lg={8}>
                <Stack spacing={2}>
                  <Typography variant="subtitle2">Search assessments</Typography>
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Search by Assessment Name"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={handleClearSearch} edge="end">
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ width: '100%', maxWidth: { xs: '100%', sm: 400, md: 500 } }}
                  />
                </Stack>
              </Grid>
            </Grid>

            <Divider />

            <Box sx={{ px: { xs: 2.5, md: 3 }, py: 3 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {loading ? (
                renderSkeleton
              ) : paginatedQuizList.length > 0 ? (
                <>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {paginatedQuizList.map((quiz) => {
                      const isCommon = quizType.find((item) => item.name === quiz)?.organization_type === 'Common';
                      const isDownloading = downloadLoading === quiz;

                      return (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={quiz}>
                          <Card
                            variant="outlined"
                            sx={{
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              borderColor: 'primary.light',
                              borderRadius: 2,
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                boxShadow: 2,
                                borderColor: 'primary.main',
                              },
                            }}
                          >
                            <CardContent sx={{ flexGrow: 1, p: 2 }}>
                              <Typography
                                variant="h6"
                                align="center"
                                color="textPrimary"
                                sx={{ mb: 2, wordBreak: 'break-word' }}
                              >
                              {quiz}
                            </Typography>
                              {isCommon && (
                                <Chip
                                  label="Public Assessment"
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  sx={{ mb: 2, display: 'block', mx: 'auto', width: 'fit-content' }}
                                />
                              )}
                            </CardContent>
                            <CardActions sx={{ px: 2, pb: 2, justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Tooltip title="Download assessment questions as Excel" arrow>
                                <span>
                                  <Button
                                    color="primary"
                                    variant="contained"
                                    size="small"
                                    startIcon={<CloudDownloadIcon />}
                                    onClick={() => handleDownload(quiz)}
                                    disabled={isDownloading}
                                    sx={{ borderRadius: '50px', px: 1.75, py: 0.5, fontSize: '0.75rem' }}
                                    aria-label="Download"
                                  >
                                    {isDownloading ? 'Downloading...' : 'Download'}
                                  </Button>
                                </span>
                              </Tooltip>
                              {isCommon ? (
                                <Tooltip title="Public assessments cannot be modified" arrow>
                                  <span>
                                    <Button
                                      color="primary"
                                      variant="contained"
                                      size="small"
                                      startIcon={<EditIcon />}
                                      disabled
                                      sx={{ borderRadius: '50px', px: 1.75, py: 0.5, fontSize: '0.75rem' }}
                                      aria-label="Modify (disabled)"
                                    >
                                      Modify
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : (
                                <AssessmentQuestionModify QuizName={quiz} />
                              )}
                            </CardActions>
                        </Card>
                      </Grid>
                      );
                    })}
                    </Grid>
                  {filteredQuizList.length > 0 && (
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-start' }}>
                    <TablePagination
                        rowsPerPageOptions={[12, 24, 48, 96]}
                      component="div"
                      count={filteredQuizList.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                          '& .MuiTablePagination-toolbar': {
                            justifyContent: 'flex-start',
                            paddingLeft: 2,
                          },
                          '& .MuiTablePagination-spacer': {
                            display: 'none',
                          },
                          '& .MuiTablePagination-selectLabel': {
                            margin: 0,
                          },
                          '& .MuiTablePagination-displayedRows': {
                            margin: 0,
                          },
                        }}
                    />
                  </Box>
                  )}
                </>
                ) : (
                renderEmptyState
                )}
            </Box>
            </Card>

          <Snackbar
            open={downloadSuccess}
            autoHideDuration={3000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <MuiAlert
              elevation={6}
              variant="filled"
              onClose={handleSnackbarClose}
              severity="success"
              sx={{ fontSize: '16px' }}
            >
              Assessment downloaded successfully!
            </MuiAlert>
          </Snackbar>
        </Container>
      )}
    </>
  );
};

export default QuizEdit;
